import classNames from 'classnames';
import _find from 'lodash/find';
import connect from 'connect-alt';
import React, { Component, PropTypes } from 'react';
import { Row, Col } from 'react-bootstrap';
import { DataStoreDispatcher } from '../../lib/frankly';
import { formatDate } from '../../utils/DateUtils';
import { weatherConditionFromIconCode } from '../../utils/WeatherUtils';
import ModuleSubMenu from '../shared/ModuleSubMenu';
import LocationSelector from './LocationSelector';
import translate from '../../utils/translate';

/**
 * Class representing the current day's detailed weather information
 *
 * Dependent on feed - wsi_endpoint
 *
 * Optional props:
 * backgroundImageUri
 *   - A string specifying the URI for an image to be displayed behind the summary information. Will always expand to
 *     the size of the component (including to the edge of the page if 'expandBackground' is true
 *   - This is the highest prioritised prop. No other prop can overrule this one, the image will always be displayed.
 *
 * weatherConditionBackground
 *   - A boolean specifying whether or not the background of the module should reflect the weather being displayed
 *     for the currently selected timeframe (current, today or tonight).
 *   - This is the second highest prioritised prop. This background will display as long as no backgroundImageUri
 *     is specified.
 *
 * backgroundColor="#0079c4"
 *   - A string specifying the background color of the block, overriding the default. This value can be a Hex value or
 *     an RGBA color.
 *     E.g. "#0079c4", "rgba(200, 200, 200, 0.5)"
 *   - Slight variations of this color are used for the location menu and search results on the LocationSelector
 *   - This is the third highest prioritised prop. A custom color will be displayed for the background as long as no
 *     backgroundImageUri is specified, and weatherConditionBackground is set to false.
 *
 * expandBackground
 *   - A boolean specifying whether or not the background should expand to the edge of the screen
 *     E.g. true, false
 *   - This can be set for a custom image, for weatherConditionBackground and for both the custom and default colors.
 *

 */
@connect('Weather')
class DailyWeatherSummary extends Component {
  static propTypes = {
    expandBackground: PropTypes.bool,
    backgroundColor: PropTypes.string,
    backgroundImageUri: PropTypes.string,
    weatherConditionBackground: PropTypes.bool,
    WeatherStore: PropTypes.object.isRequired
  }

  static contextTypes = {
    flux: PropTypes.object.isRequired,
    config: PropTypes.object.isRequired,
  }

  state = {
    temperatureFormat: 'F',
    timeFrameDisplayValue: 'current'
  }

  componentWillMount() {
    this._weatherDataStoreDispatcher = new DataStoreDispatcher('franklyinc.com/weather');

    this._weatherDataStoreDispatcher.onChange('temperatureFormat', (val) => {
      this.setState({
        temperatureFormat: val
      });
    });
  }

  componentWillUnmount() {
    this._weatherDataStoreDispatcher.off();
    this._weatherDataStoreDispatcher = null;
  }

  _weatherDataStoreDispatcher = null;

  _setDisplayTimeFrame(val, event) {
    this.setState({
      timeFrameDisplayValue: val
    });

    event.stopPropagation();
  }

  _getClassNames(defaultNames, timeFrameValue) {
    let timeFrameClassNames = defaultNames;

    if (this.state.timeFrameDisplayValue === timeFrameValue) {
      timeFrameClassNames += ' is-active';
    }

    return timeFrameClassNames;
  }

  _handleTemperatureFormatClick(format) {
    this._weatherDataStoreDispatcher.set('temperatureFormat', format);
  }

  render() {
    const {
      expandBackground,
      backgroundColor,
      backgroundImageUri,
      weatherConditionBackground,
      WeatherStore: { wsi: { data: weatherData = {} } = {} } } = this.props;
    const {
      temperatureFormat,
      timeFrameDisplayValue } = this.state;
    const {
      Cities: {
        City: {
          CurrentObservation: currentObservation = null,
          DailyForecast: {
            Day: [dailyForecast = null, ] = []
          } = {}
        } = {}
      } = {}
    } = weatherData;

    const { config: { appOptions: {language} } } = this.context;

    if (!currentObservation || !dailyForecast) {
      return null;
    }

    const { '@IconCode': currentIconCode } = currentObservation;
    const {
      '@IconCode': todayIconCode,
      '@IconCodeNight': tonightIconCode,
    } = dailyForecast;
    const componentClassnames = classNames('DailyWeatherSummary', 'DailyWeatherSummary-wrapper');
    const layoutClassnames = classNames('DailyWeatherSummary-layout', {'DailyWeatherSummary--expanded': expandBackground});
    let weatherCode;
    let backgroundClasses;
    let customBackgroundStyles = {};
    let weatherCondition = null;
    let isImgBackground = false;

    if (timeFrameDisplayValue === 'current') {
      weatherCode = currentIconCode;
    } else if (timeFrameDisplayValue === 'today') {
      weatherCode = todayIconCode;
    } else {
      weatherCode = tonightIconCode;
    }

    // Note that the hierarchy
    if (weatherConditionBackground) {
      weatherCondition = weatherConditionFromIconCode(weatherCode);
      isImgBackground = true;
    }

    if (backgroundColor) {
      customBackgroundStyles['backgroundColor'] = backgroundColor;
    }

    if (backgroundImageUri) {
      customBackgroundStyles['backgroundImage'] = 'url(' + backgroundImageUri + '?auto=webp&disable=upscale&fit=crop&width=1470)';
      isImgBackground = true;
    }

    backgroundClasses = classNames(
      'DailyWeatherSummary-background',
      (weatherCondition ? 'DailyWeatherSummary-background-' + weatherCondition : ''),
      { expandToEdges: expandBackground });

    return (
      <div className={componentClassnames}>
        <div className={layoutClassnames}>
          <LocationSelector backgroundColor={backgroundColor}/>
          <div className="DailyWeatherSummary-infoContainer">
            <div className="DailyWeatherSummary-keyInfoBlock DailyWeatherSummary-block">
              <DailyForecastKeyInformation
                weatherData={weatherData}
                onTemperatureFormatClick={format => this._handleTemperatureFormatClick(format)}
                temperatureFormat={temperatureFormat}
                timeFrameDisplayValue={timeFrameDisplayValue}
                />
              <ModuleSubMenu className="DailyWeatherSummary-timeFrameOptionsBlock" isVerticalOnWideScreen>
                <li className={this._getClassNames("DailyWeatherSummary-timeFrameOption", "current") }>
                  <a onClick={this._setDisplayTimeFrame.bind(this, 'current') }>{translate('Current', language)}</a>
                </li>
                <li className={this._getClassNames("DailyWeatherSummary-timeFrameOption", "today") }>
                  <a onClick={this._setDisplayTimeFrame.bind(this, 'today') }>{translate('Today', language)}</a>
                </li>
                <li className={this._getClassNames("DailyWeatherSummary-timeFrameOption", "tonight") }>
                  <a onClick={this._setDisplayTimeFrame.bind(this, 'tonight') }>{translate('Tonight', language)}</a>
                </li>
              </ModuleSubMenu>
            </div>
            <div className="DailyWeatherSummary-detailsBlock DailyWeatherSummary-block">
              <DailyForecastDetailsBlock
                lang={language}
                weatherData={weatherData}
                temperatureFormat={temperatureFormat}
                timeFrameDisplayValue={timeFrameDisplayValue}
                dividingLinesOnly={isImgBackground && !expandBackground}
                noInternalBorders={!isImgBackground || expandBackground}
                simpleInsideLines={isImgBackground && expandBackground}
                />
            </div>
          </div>
        </div>
        <span className={backgroundClasses} style={customBackgroundStyles} />
      </div>
    );
  }
}

/**
 * Class representing the largest and key pieces of information for the Daily Weather Summary.
 */
class DailyForecastKeyInformation extends Component {
  static propTypes = {
    weatherData: PropTypes.object.isRequired,
    onTemperatureFormatClick: PropTypes.func.isRequired,
    timeFrameDisplayValue: PropTypes.string.isRequired,
    temperatureFormat: PropTypes.string
  }

  static contextTypes = {
    location: PropTypes.object.isRequired,
    config: PropTypes.object.isRequired
  }

  _setTemperatureFormat(newTempFormat) {
    const { onTemperatureFormatClick } = this.props;

    onTemperatureFormatClick(newTempFormat);
  }

  render() {
    const { weatherData, timeFrameDisplayValue, temperatureFormat } = this.props;
    const {
      config: {
        appOptions: { language },
        affiliate: { locations: stationLocations = [] } = {},
      },
      location
    } = this.context;
    const {
      Cities: {
        City: {
          CurrentObservation: currentObservation = null,
          DailyForecast: { Day: [dailyForecast = null, ] = [] } = {}
        } = {}
      } = {}
    } = weatherData;
    const {
      '@TempF': currentTempF,
      '@TempC': currentTempC,
      '@IconCode': currentIconCode,
      '@Sky': currentSky,
      '@FeelsLikeF': currentFeelsLikeF,
      '@FeelsLikeC': currentFeelsLikeC
    } = currentObservation;
    const {
      '@IconCode': todayIconCode,
      '@HiTempF': HiTempF,
      '@HiTempC': HiTempC,
      '@SkyText': todaySky,
      '@LoTempF': loTempF,
      '@LoTempC': loTempC,
      '@PhraseDay': todayPhraseF,
      '@PhraseDayC': todayPhraseC,
      '@PhraseNight': tonightPhraseF,
      '@PhraseNightC': tonightPhraseC,
      '@IconCodeNight': tonightIconCode,
      '@SkyTextNight': tonightSky,
    } = dailyForecast;

    let feelsLike = '';
    let forecastPhrase = '';
    let displayTemperature;
    let skyText;
    let iconCode;
    let showWeatherPhrase = false;

    // Only show the weather phrase if the current location isn't a default location in the config
    if (!_find(stationLocations, { 'zipcode': Number(location.zipcode)})){
      showWeatherPhrase = true;
    }

    if (timeFrameDisplayValue === 'current') {
      displayTemperature = (temperatureFormat !== 'C') ? currentTempF : currentTempC;
      skyText = currentSky;
      feelsLike = (temperatureFormat !== 'C') ? currentFeelsLikeF : currentFeelsLikeC;
      iconCode = currentIconCode;
    } else if (timeFrameDisplayValue === 'today') {
      displayTemperature = (temperatureFormat !== 'C') ? HiTempF : HiTempC;
      skyText = todaySky;
      iconCode = todayIconCode;
      forecastPhrase = (temperatureFormat !== 'C') ? todayPhraseF : todayPhraseC;
    } else {
      displayTemperature = (temperatureFormat !== 'C') ? loTempF : loTempC;
      skyText = tonightSky;
      iconCode = tonightIconCode;
      forecastPhrase = (temperatureFormat !== 'C') ? tonightPhraseF : tonightPhraseC;
    }

    return (
      <div className="DailyForecastKeyInformation">
        <div className="DailyForecastKeyInformation-tempDisplay">
          <span className={'weather-icon weather-icon-' + iconCode}/>
          <span className="temperature" >{displayTemperature}</span>
          <div className="DailyForecastKeyInformation-temperatureChoice">
            <span className={'temperature-choice-label line-after' + ((temperatureFormat !== 'C') ? ' active' : '') }
              dangerouslySetInnerHTML={{ __html: 'F &deg;' }}
              onClick={this._setTemperatureFormat.bind(this, 'F') }
              />
            <span className={'temperature-choice-label ' + ((temperatureFormat === 'C') ? ' active' : '') }
              dangerouslySetInnerHTML={{ __html: 'C &deg;' }}
              onClick={this._setTemperatureFormat.bind(this, 'C') }
              />
          </div>
        </div>
        <div className="DailyForecastKeyInformation-tempDescription">
          <span className="DailyForecastKeyInformation-skyText">{skyText}</span>
          {(timeFrameDisplayValue === 'current') ?
            [<span key="1">{translate('Feels Like', language)} </span>,
              <span key="3" className="DailyForecastKeyInformation-feelsLikeTemp" dangerouslySetInnerHTML={{ __html: feelsLike + '&deg;' }} />]
            : null
          }
        </div>
        { forecastPhrase && showWeatherPhrase ?
          <div className="DailyForecastKeyInformation-forecastPhrase">
            {forecastPhrase}
          </div> : null
        }
      </div>
    );
  }
}

/**
 * Class representing the granular pieces of information for the Daily Weather Summary.
 */
class DailyForecastDetailsBlock extends Component {
  static propTypes = {
    weatherData: PropTypes.object.isRequired,
    timeFrameDisplayValue: PropTypes.string.isRequired,
    temperatureFormat: PropTypes.string,
    dividingLinesOnly: PropTypes.bool,
    noInternalBorders: PropTypes.bool,
    simpleInsideLines: PropTypes.bool,
    lang: PropTypes.string
  };

  render() {
    const { weatherData, timeFrameDisplayValue, temperatureFormat, dividingLinesOnly, noInternalBorders, simpleInsideLines, lang } = this.props;
    const {
      Cities: {
        City: {
          CurrentObservation: currentObservation = null,
          DailyForecast: { Day: [dailyForecast = null, ] = [] } = {}
        } = {}
      } = {}
    } = weatherData;
    const {
      '@Pressure': pressure,
      '@HeatIdxF': heatIndexF,
      '@HeatIdxC': heatIndexC,
      '@WndChillF': windChillF,
      '@WndChillC': windChillC,
      '@RelHumidity': currentRelHumidity,
      '@WndDirCardinal': currentWndDirCardinal,
      '@WndSpdMph': currentWndSpdMph
    } = currentObservation;
    const {
      '@PrecipChance': currentPrecipChance,
      '@ValidDateLocal': dateTime,
      '@Sunrise': sunrise,
      '@Sunset': sunset,
      '@UvIdx': uvIndex,
      '@UvDescr': uvDescription,
      '@PrecipChanceDay': precipChanceDay,
      '@RelHumidity': todayRelHumidity,
      '@WndSpdMph': todayWndSpdMph,
      '@WndDirCardinal': todayWndDirCardinal,
      '@Moonrise': moonrise,
      '@Moonset': moonset,
      '@MoonPhaseText': moonPhase,
      '@PrecipChanceNight': precipitationNight,
      '@RelHumidityNight': tonightRelHumidity,
      '@WndSpdMphNight': tonightWndSpdMph,
      '@WndDirCardinalNight': tonightWndDirCardinal
      } = dailyForecast;
    const heatIndex = (temperatureFormat !== 'C') ? heatIndexF : heatIndexC;
    const windChill = (temperatureFormat !== 'C') ? windChillF : windChillC;
    const detailsBlockClasses = classNames(
      'DailyForecastDetailsBlock',
      { 'DailyForecastDetailsBlock--dividingLinesOnly': dividingLinesOnly },
      { 'DailyForecastDetailsBlock--noInternalBorders': noInternalBorders },
      { 'DailyForecastDetailsBlock--simpleInsideLines': simpleInsideLines }
    );

    let windSpeed;
    let windDirection;
    let humidity;
    let precipitation;

    if (timeFrameDisplayValue === 'current') {
      precipitation = currentPrecipChance;
      windSpeed = currentWndSpdMph;
      windDirection = currentWndDirCardinal;
      humidity = currentRelHumidity;
    } else if (timeFrameDisplayValue === 'today') {
      precipitation = precipChanceDay;
      windSpeed = todayWndSpdMph;
      windDirection = todayWndDirCardinal;
      humidity = todayRelHumidity;
    } else {
      precipitation = precipitationNight;
      windSpeed = tonightWndSpdMph;
      windDirection = tonightWndDirCardinal;
      humidity = tonightRelHumidity;
    }

    return (
      <div className={detailsBlockClasses}>
        <div className="DailyForecastDetailsBlock-infoCell">
          {
            timeFrameDisplayValue === 'current' ?
              <DetailRow title={translate('Pressure', lang)} value={pressure} valueClassName={classNames({ 'weather-pressure': pressure })} /> :
              <DetailRow title={translate('Precip.', lang)} value={precipitation} valueClassName={classNames({ 'weather-precipitation weather-percentage': precipitation })} />
          }
          <DetailRow title={translate('Wind', lang)} value={windSpeed ? windDirection + ' ' + windSpeed + ' MPH' : null} />
        </div>

        <div className="DailyForecastDetailsBlock-infoCell">
          <DetailRow title={translate('Humidity', lang)} value={humidity} valueClassName={classNames({ 'weather-percentage': humidity })}/>
          {
            timeFrameDisplayValue === 'tonight' ?
              <DetailRow title={translate('Moon Phase', lang)} value={moonPhase} /> :
              <DetailRow title={translate('UV Index', lang)} value={uvIndex ? uvIndex + ' ' + uvDescription : null} />
          }
        </div>
        {timeFrameDisplayValue === 'current' ?
          <div className="DailyForecastDetailsBlock-infoCell">
            <DetailRow title={translate('Heat Index', lang)} value={heatIndex ? heatIndex + '°' : null} />
            <DetailRow title={translate('Wind Chill', lang)} value={windChill ? windChill + '°' : null} />
          </div> : null }
        <div className="DailyForecastDetailsBlock-infoCell blockAtSmallWidth">
          <TimeDetailRow title={translate('Sunrise', lang)} validDateLocal={dateTime} value={sunrise} valueClassName="weather-sun sunrise" />
          <TimeDetailRow title={translate('Sunset', lang)} validDateLocal={dateTime} value={sunset} valueClassName="weather-sun sunset" />
        </div>
        <div className="DailyForecastDetailsBlock-infoCell blockAtSmallWidth">
          <TimeDetailRow title={translate('Moonrise', lang)} validDateLocal={dateTime} value={moonrise} valueClassName="weather-moon moonrise" />
          <TimeDetailRow title={translate('Moonset', lang)} validDateLocal={dateTime} value={moonset} valueClassName="weather-moon moonset" />
        </div>
      </div>
    );
  }
}

class DetailRow extends Component {
  static propTypes = {
    title: PropTypes.string.isRequired,
    value: PropTypes.string,
    valueClassName: PropTypes.string,
    spans: PropTypes.object
  }

  static defaultProps = {
    spans: {
      xs: 6
    }
  }

  render() {
    const { title, value, valueClassName, spans } = this.props;

    return (
      <Row>
        <Col {...spans} className="infoTitle col-xl-12">{title}</Col>
        <Col {...spans} className={classNames('infoValue col-xl-12', valueClassName)} >{value || 'N/A'}</Col>
      </Row>
    );
  }
}

class TimeDetailRow extends Component {
  static propTypes = {
    title: PropTypes.string.isRequired,
    value: PropTypes.string,
    valueClassName: PropTypes.string,
    validDateLocal: PropTypes.string.isRequired
  }

  render() {
    const { validDateLocal, value } = this.props;

    return (
      <DetailRow
        {...this.props}
        spans={{
          xs: 12,
          lg: 6
        }}
        value={value ? formatDate(validDateLocal.split(' ')[0] + ' ' + value, 'h:mm A') : null }
        />
    );
  }
}

export default DailyWeatherSummary;



// WEBPACK FOOTER //
// ./app/components/weather/DailyWeatherSummary.js